import { zodResolver } from '@hookform/resolvers/zod';
import Grid from '@mui/material/Grid';
import {
  Controller,
  type DefaultValues,
  type FieldErrors,
  type SubmitHandler,
  useForm,
} from 'react-hook-form';
import Button, {
  ButtonSize,
  ButtonVariant,
} from '@/components/BaseLibrary/Button/Button';
import Dropdown from '@/components/BaseLibrary/Dropdown/Dropdown';
import FormInput, {
  InputType,
} from '@/components/BaseLibrary/FormInput/FormInput';
import { RadioGroup } from '@/components/BaseLibrary/RadioGroup/RadioGroup';
import GridContainer from '@/components/Layout/GridContainer/GridContainer';
import useViewPortSize from '@/hooks/useViewportSize';
import useGlobalStyles from '@/styles/global.styles';
import { trackEvents } from '@/utils/analytics';
import { setImgWidth, setSrcSetImg } from '@/utils/frontify';
import { InputName, OwnHome } from '@/utils/validation/inputs';
import type { PaidLanderFields } from '../PaidLander.types';
import {
  QualifyStepSchemaMode,
  type QualifyStepSchemaType,
  qualifyStepSchema,
} from '../schema';
import useStyles from './QualifyStep.styles';

const billAmounts: string[] = [
  '$0-50',
  '$51-100',
  '$101-150',
  '$151-200',
  '$201-300',
  '$301-400',
  '$401-500',
  '$501-600',
  '$601-700',
  '$701-800',
  '$801+',
];

// we use hideBillInput and showHomeownerQuestion cms variables to set default mode value.
// it's used to narrow down form schema type and control field visibility
const getDefaultValues = ({
  hideBillInput,
  showHomeownerQuestion,
}: {
  hideBillInput: boolean;
  showHomeownerQuestion: boolean | undefined;
}): DefaultValues<QualifyStepSchemaType> => {
  if (hideBillInput && showHomeownerQuestion) {
    return {
      mode: QualifyStepSchemaMode.ZIP_AND_OWNHOME,
      [InputName.OWN_HOME]: OwnHome.OWN,
    };
  }

  if (!hideBillInput && !showHomeownerQuestion) {
    return {
      mode: QualifyStepSchemaMode.ZIP_AND_BILL,
      [InputName.LAST_BILL]: billAmounts[4],
    };
  }

  if (hideBillInput && !showHomeownerQuestion) {
    return { mode: QualifyStepSchemaMode.ZIP_ONLY };
  }

  return {
    mode: QualifyStepSchemaMode.ALL_FIELDS,
    [InputName.OWN_HOME]: OwnHome.OWN,
    [InputName.LAST_BILL]: billAmounts[4],
  };
};

// acts as a type guard for narrowing down error object union type + controls field visibility
const shouldShowLastBillField = (
  mode: string,
  _errors: FieldErrors<QualifyStepSchemaType>
): _errors is FieldErrors<
  Extract<
    QualifyStepSchemaType,
    {
      mode:
        | QualifyStepSchemaMode.ZIP_AND_BILL
        | QualifyStepSchemaMode.ALL_FIELDS;
    }
  >
> => {
  return (
    mode === QualifyStepSchemaMode.ZIP_AND_BILL ||
    mode === QualifyStepSchemaMode.ALL_FIELDS
  );
};
const shouldShowOwnsHomeField = (
  mode: string,
  _errors: FieldErrors<QualifyStepSchemaType>
): _errors is FieldErrors<
  Extract<
    QualifyStepSchemaType,
    {
      mode:
        | QualifyStepSchemaMode.ZIP_AND_OWNHOME
        | QualifyStepSchemaMode.ALL_FIELDS;
    }
  >
> => {
  return (
    mode === QualifyStepSchemaMode.ZIP_AND_OWNHOME ||
    mode === QualifyStepSchemaMode.ALL_FIELDS
  );
};

export default function QualifyStep({
  firstStepHeader,
  firstStepSubHeading,
  firstStepImage,
  firstStepImageAltText,
  nextStepText,
  showHomeownerQuestion,
  zipCodeLabel = 'What is your zip code?',
  averageElectricBillLabel = 'What is your average electric bill?',
  ownYourHomeLabel = 'Do you own your home?',
  nextPage,
  isVisible,
  isOnDark = false,
  fixedGrid = false,
  hideBillInput = false,
  reverseLayout = false,
  hideStepCounter = false,
  hideImageOnMobile = false,
  zipInputRef,
}: PaidLanderFields & {
  nextPage: (arg: QualifyStepSchemaType) => void;
  isVisible: boolean;
  zipInputRef?: React.Ref<HTMLInputElement>;
}) {
  const {
    watch,
    register,
    control,
    trigger,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<QualifyStepSchemaType>({
    resolver: zodResolver(qualifyStepSchema),
    mode: 'onTouched',
    defaultValues: getDefaultValues({ hideBillInput, showHomeownerQuestion }),
  });
  const { classes, cx } = useStyles({
    isOnDark,
    hideImageOnMobile,
  });
  const { classes: globalClasses } = useGlobalStyles({ fixed: false });
  const { isMobile } = useViewPortSize();
  const imageSize = isMobile ? 768 : 1440;

  const mode = watch('mode');

  const onSubmit: SubmitHandler<QualifyStepSchemaType> = (data) => {
    trackEvents({
      event: 'Form_cta_click',
      Form_tab: 'Zip Code/Electrical Bill/Home Ownership',
      Form_step: 1,
      click_text: 'Next',
    });

    nextPage(data);
  };

  return (
    <GridContainer
      gridProps={{
        className: cx(
          isVisible ? classes.visible : classes.hidden,
          reverseLayout ? classes.reverseLayout : classes.layout
        ),
      }}
      fixed={fixedGrid}
    >
      <Grid
        className={cx(reverseLayout && classes.reverseContent)}
        size={{
          xs: 12,
          sm: 9,
          md: 6,
        }}
      >
        {!hideStepCounter && (
          <div className={cx(globalClasses.chip, classes.step)}>
            step 1 of 2
          </div>
        )}
        <div
          className={cx(
            fixedGrid ? globalClasses.h2 : globalClasses.h1,
            classes.heading
          )}
        >
          {firstStepHeader}
        </div>
        <div className={cx(globalClasses.bodyLg, classes.subheading)}>
          {firstStepSubHeading}
        </div>
        <div className={classes.inputGroup}>
          <input type='hidden' {...register('mode')} />
          <div className={classes.inputContainer}>
            <FormInput
              label={zipCodeLabel}
              testId='zip-input'
              type={InputType.ZIP}
              inputMode='numeric'
              error={errors[InputName.ZIP_CODE]?.message}
              isOnDark={isOnDark}
              name={InputName.ZIP_CODE}
              register={register}
              inputRef={zipInputRef}
            />
          </div>
          {shouldShowLastBillField(mode, errors) && (
            <div className={classes.inputContainer}>
              <Controller
                name={InputName.LAST_BILL}
                control={control}
                render={({ field }) => (
                  <Dropdown
                    {...field}
                    defaultOption={field.value}
                    isOnDark={isOnDark}
                    label={averageElectricBillLabel || ''}
                    options={billAmounts}
                    error={errors[InputName.LAST_BILL]?.message}
                  />
                )}
              />
            </div>
          )}
          {shouldShowOwnsHomeField(mode, errors) && (
            <div className={classes.radioContainer}>
              <RadioGroup
                legend={ownYourHomeLabel}
                options={[
                  { label: 'Yes', value: OwnHome.OWN },
                  { label: 'No', value: OwnHome.RENT },
                ]}
                register={register}
                trigger={trigger}
                name={InputName.OWN_HOME}
                errors={errors}
                isOnDark={isOnDark}
                gaEvent={{
                  event: 'Form_cta_click',
                  Form_tab: 'Home Ownership',
                  Form_step: 1,
                  click_text: '',
                }}
                isSmall={true}
              />
            </div>
          )}
        </div>
        <Button
          testId='next-button'
          type='submit'
          variant={ButtonVariant.PRIMARY}
          label={nextStepText}
          size={ButtonSize.MD}
          className={classes.button}
          onClick={handleSubmit(onSubmit)}
          disabled={!isValid}
          isOnDark={isOnDark}
        />
      </Grid>
      <Grid
        className={classes.hideImageOnMobile}
        size={{
          xs: 12,
          md: 6,
        }}
      >
        {/** biome-ignore lint/performance/noImgElement: Automated migration to Biome */}
        <img
          src={setImgWidth(firstStepImage?.[0].preview_url, imageSize)}
          srcSet={setSrcSetImg(firstStepImage?.[0].preview_url, imageSize)}
          className={classes.landerImage}
          alt={firstStepImageAltText}
          width={imageSize}
        />
      </Grid>
    </GridContainer>
  );
}
